import React, { useEffect, useState } from "react";
import { ShieldAlert, FlaskConical, X } from "lucide-react";

/**
 * UK research-supplier age & professional-research gate.
 *
 * Required by best practice for research-peptide suppliers (MHRA Blue Guide
 * paragraph 5.3 — verify customers are not the general public). Stores the
 * acknowledgement in localStorage so it only appears once per device.
 *
 * Hidden from /admin and /staff-portal-* routes (those users are us).
 */
const KEY = "pg_age_gate_v1";
const EXCLUDED_PREFIXES = ["/admin", "/staff-portal-"];

export default function AgeGate() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (EXCLUDED_PREFIXES.some((p) => path.startsWith(p))) return;
    try {
      const stored = localStorage.getItem(KEY);
      if (!stored) setOpen(true);
    } catch {
      setOpen(true);
    }
  }, []);

  const accept = () => {
    try {
      localStorage.setItem(KEY, JSON.stringify({ accepted: true, ts: Date.now() }));
    } catch {}
    setOpen(false);
  };

  const decline = () => {
    // Send the user away — explicit "we don't want non-research traffic".
    window.location.href = "https://www.google.com";
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Age and research-use confirmation"
      data-testid="age-gate-modal"
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      style={{ background: "rgba(11,18,32,0.92)", backdropFilter: "blur(8px)" }}
    >
      <div
        className="bg-[#0F172A] border border-[#00C2D6]/40 rounded-sm w-full max-w-lg p-6 sm:p-8 shadow-[0_0_60px_-10px_rgba(0,194,214,0.4)]"
      >
        <div className="flex items-center gap-2 mb-3">
          <FlaskConical size={18} className="text-[#A6FF00]" />
          <span className="pg-mono text-[10px] tracking-[0.2em] text-[#5EEAD4] uppercase">
            For laboratory research only
          </span>
        </div>
        <h2
          className="text-[#F7FAFC] text-2xl sm:text-3xl pg-display leading-tight mb-4"
          style={{ fontFamily: "'Exo 2'" }}
        >
          Confirm you are 18+ and visiting in a professional research capacity
        </h2>
        <p className="text-[#9CA8B8] text-[13.5px] leading-relaxed mb-3">
          Peptigen Labs Ltd supplies research-use-only laboratory materials to
          qualified researchers, laboratory technicians and scientific buyers.
          Our products are <strong className="text-[#F7FAFC]">not medicines</strong>{" "}
          and are not intended for human or animal consumption.
        </p>
        <p className="text-[#9CA8B8] text-[13px] leading-relaxed mb-6">
          By continuing you confirm that:
        </p>
        <ul className="space-y-2 mb-6 text-[13px] text-[#F7FAFC]">
          <li className="flex items-start gap-2">
            <ShieldAlert size={13} className="text-[#A6FF00] shrink-0 mt-1" />
            <span>You are 18 years of age or older.</span>
          </li>
          <li className="flex items-start gap-2">
            <ShieldAlert size={13} className="text-[#A6FF00] shrink-0 mt-1" />
            <span>You are accessing this site in a professional research capacity.</span>
          </li>
          <li className="flex items-start gap-2">
            <ShieldAlert size={13} className="text-[#A6FF00] shrink-0 mt-1" />
            <span>You will use any products purchased exclusively in a controlled laboratory environment.</span>
          </li>
        </ul>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={accept}
            data-testid="age-gate-accept"
            className="text-[10px] font-extrabold tracking-[0.18em] uppercase rounded-sm py-3 px-4 sm:flex-1"
            style={{ background: "#A6FF00", color: "#0B1220", border: 0, cursor: "pointer" }}
          >
            I confirm — enter site
          </button>
          <button
            type="button"
            onClick={decline}
            data-testid="age-gate-decline"
            className="pg-btn-ghost text-[10px] tracking-[0.18em] uppercase sm:w-auto"
          >
            <X size={12} className="inline -mt-0.5" /> Leave site
          </button>
        </div>

        <div className="mt-5 pt-4 border-t border-[#1E2E44] text-[11px] text-[#9CA8B8] leading-relaxed">
          By continuing you agree to our{" "}
          <a href="/legal/terms" className="text-[#5EEAD4] hover:underline">Terms &amp; Conditions</a>{" "}
          and{" "}
          <a href="/legal/research-use" className="text-[#5EEAD4] hover:underline">Research-Use Statement</a>.
        </div>
      </div>
    </div>
  );
}
